import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  ArrowLeft, User, Phone, EnvelopeSimple, MapPin, FileText,
  CurrencyEur, Calendar, Check, Clock, Download, Pencil, WhatsappLogo
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '../config/api';
import WhatsAppSendDialog from '../components/WhatsAppSendDialog';

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [client, setClient] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const { data } = await axios.get(`${API}/clients/${id}`);
        setClient(data || {})
      } catch (err) {
        toast.error(t('clientDetail.loadError'));
        navigate('/clients');
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [id, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const locale = i18n.language === 'es' ? 'es-ES' : 'it-IT';
    return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const statusLabels = {};
  ['draft','sent','pending','accepted','rejected','won','lost'].forEach(s => {
    statusLabels[s] = t(`clientDetail.statusLabels.${s}`);
  });
  const statusColors = {
    draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700',
    pending: 'bg-yellow-100 text-yellow-700', accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700', won: 'bg-green-100 text-green-700',
    lost: 'bg-red-100 text-red-700'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/clients')} className="text-[#4A5568]" data-testid="back-button">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A]">{client.name}</h1>
            <p className="text-[#4A5568] mt-1">{t('clientDetail.clientSince')} {formatDate(client.created_at)}</p>
          </div>
        </div>
        <div className="flex gap-3">
          {client.phone && (
            <Button
              variant="outline"
              onClick={() => setWaOpen(true)}
              className="border-[#25D366] text-[#128C4B]"
              data-testid="whatsapp-client-button"
            >
              <WhatsappLogo size={18} weight="fill" className="mr-2 text-[#25D366]" />
              WhatsApp
            </Button>
          )}
          <Button
            onClick={() => navigate(`/quotes/new?client=${client.id}`)}
            className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
            data-testid="new-quote-for-client"
          >
            <FileText size={18} className="mr-2" />
            {t('clientDetail.newQuote')}
          </Button>
        </div>
      </div>

      <WhatsAppSendDialog open={waOpen} onOpenChange={setWaOpen} client={client} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Info */}
        <div className="space-y-6">
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-3">
              <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
                <User size={18} className="text-[#0033A0]" />
                {t('clientDetail.info')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {client.phone && (
                <div className="flex items-center gap-3">
                  <Phone size={16} className="text-[#4A5568]" />
                  <a href={`tel:${client.phone}`} className="text-sm text-[#0033A0] hover:underline">{client.phone}</a>
                </div>
              )}
              {client.email && (
                <div className="flex items-center gap-3">
                  <EnvelopeSimple size={16} className="text-[#4A5568]" />
                  <a href={`mailto:${client.email}`} className="text-sm text-[#0033A0] hover:underline">{client.email}</a>
                </div>
              )}
              {client.address && (
                <div className="flex items-center gap-3">
                  <MapPin size={16} className="text-[#4A5568]" />
                  <span className="text-sm text-[#0A0F1A]">{client.address}</span>
                </div>
              )}
              {client.notes && (
                <div className="pt-2 border-t border-[#E2E8F0]">
                  <p className="text-xs text-[#4A5568] font-medium mb-1">{t('clientDetail.notes')}</p>
                  <p className="text-sm text-[#0A0F1A]">{client.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats */}
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-3">
              <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
                <CurrencyEur size={18} className="text-[#0033A0]" />
                {t('clientDetail.stats')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#4A5568]">{t('clientDetail.quotes')}</span>
                <span className="font-semibold text-[#0A0F1A]">{client.stats?.total_quotes || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#4A5568]">{t('clientDetail.totalValue')}</span>
                <span className="font-semibold text-[#0A0F1A]">{'\u20AC'}{(client.stats?.total_value || 0).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'it-IT', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#4A5568]">{t('clientDetail.wonValue')}</span>
                <span className="font-semibold text-green-700">{'\u20AC'}{(client.stats?.won_value || 0).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'it-IT', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#4A5568]">{t('clientDetail.openFollowUps')}</span>
                <span className="font-semibold text-amber-600">{client.stats?.pending_follow_ups || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quote History */}
        <div className="lg:col-span-2">
          <Card className="border border-[#E2E8F0] rounded-sm">
            <CardHeader className="border-b border-[#E2E8F0] pb-3">
              <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
                <FileText size={18} className="text-[#0033A0]" />
                {t('clientDetail.quoteHistory')} ({client.quotes?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {client.quotes && client.quotes.length > 0 ? (
                <div className="divide-y divide-[#E2E8F0]">
                  {(client.quotes || []).map(quote => (
                    <div key={quote.id} className="p-4 hover:bg-[#F4F6F8] transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <Link to={`/quotes/${quote.id}`} className="font-medium text-[#0033A0] hover:underline" data-testid={`quote-link-${quote.id}`}>
                              {quote.quote_number}
                            </Link>
                            <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${statusColors[quote.status] || 'bg-gray-100 text-gray-700'}`}>
                              {statusLabels[quote.status] || quote.status}
                            </span>
                          </div>
                          <p className="text-xs text-[#4A5568] mt-1">
                            {formatDate(quote.created_at)} | {(quote.items || []).length} {t('clientDetail.products')}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-[#0A0F1A]">{'\u20AC'}{(quote.total || 0).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'it-IT', { minimumFractionDigits: 2 })}</span>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/quotes/${quote.id}`)} className="text-[#4A5568]">
                            <ArrowLeft size={14} className="rotate-180" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <FileText size={32} className="text-[#E2E8F0] mx-auto" />
                  <p className="text-sm text-[#4A5568] mt-2">{t('clientDetail.noQuotes')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Follow-ups */}
          {client.follow_ups && client.follow_ups.length > 0 && (
            <Card className="border border-[#E2E8F0] rounded-sm mt-6">
              <CardHeader className="border-b border-[#E2E8F0] pb-3">
                <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
                  <Clock size={18} className="text-[#0033A0]" />
                  Solleciti ({client.follow_ups.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-[#E2E8F0]">
                  {(client.follow_ups || []).map(fu => (
                    <div key={fu.id} className={`p-4 flex items-center justify-between ${fu.completed ? 'opacity-50' : ''}`}>
                      <div className="flex items-center gap-3">
                        {fu.completed ? (
                          <Check size={16} className="text-green-600" />
                        ) : (
                          <Clock size={16} className="text-amber-600" />
                        )}
                        <div>
                          <p className="text-sm text-[#0A0F1A]">{fu.message}</p>
                          <p className="text-xs text-[#4A5568]">{formatDate(fu.due_date)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

